import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { ApiAuthService } from '../services/api-auth.service';
import { AuthService } from '../services/auth.service';

/**
 * BFF auth endpoints authenticate via the HttpOnly refresh cookie; they must
 * never receive a Bearer header and must bypass the 401-refresh recovery path
 * (a failed refresh cannot re-refresh itself).
 */
const BFF_AUTH_PATH = '/bff/auth/';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const apiAuthService = inject(ApiAuthService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (req.url.includes(BFF_AUTH_PATH)) {
    return next(req);
  }

  const token = apiAuthService.getAccessToken();

  if (token) {
    req = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return apiAuthService.refreshToken().pipe(
          switchMap(() => {
            const newToken = apiAuthService.getAccessToken();
            const newReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
            });
            return next(newReq);
          }),
          catchError((refreshError) => {
            apiAuthService.clearToken().subscribe({ error: () => {} });
            authService.clearAuthState();
            if (!router.url.startsWith('/login')) {
              router.navigate(['/login']);
            }
            return throwError(() => refreshError);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
